
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Trophy, Calendar, DollarSign, TrendingUp } from 'lucide-react';

const GameHistory = () => {
  const [historyPeriod, setHistoryPeriod] = useState('week');

  const gameHistory = [
    {
      id: 1,
      date: '2025-01-23',
      game: 'PowerBall',
      numbers: [7, 14, 23, 31, 42, 45],
      powerball: 12,
      matches: 3,
      winnings: 25,
      betAmount: 5,
      status: 'won'
    },
    {
      id: 2,
      date: '2025-01-21',
      game: 'Oz Lotto',
      numbers: [3, 18, 26, 33, 39, 44, 8],
      matches: 2,
      winnings: 0,
      betAmount: 10,
      status: 'lost'
    },
    {
      id: 3,
      date: '2025-01-18',
      game: 'Saturday Lotto',
      numbers: [9, 15, 22, 28, 35, 41],
      matches: 4,
      winnings: 150,
      betAmount: 5,
      status: 'won'
    },
    {
      id: 4,
      date: '2025-01-16',
      game: 'PowerBall',
      numbers: [1, 8, 19, 27, 36, 43],
      powerball: 5,
      matches: 1,
      winnings: 0,
      betAmount: 5,
      status: 'lost'
    },
    {
      id: 5,
      date: '2025-01-14',
      game: 'Oz Lotto',
      numbers: [11, 16, 24, 29, 38, 42, 6],
      matches: 5,
      winnings: 2500,
      betAmount: 25,
      status: 'won'
    }
  ];

  const totalWinnings = gameHistory.reduce((sum, game) => sum + game.winnings, 0);
  const totalSpent = gameHistory.reduce((sum, game) => sum + game.betAmount, 0);
  const netProfit = totalWinnings - totalSpent;
  const winRate = (gameHistory.filter(game => game.status === 'won').length / gameHistory.length * 100).toFixed(1);

  return (
    <div className="space-y-6">
      {/* Statistics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <DollarSign className="w-6 h-6 text-green-600 mx-auto mb-2" />
            <p className="text-sm text-gray-600">Total Winnings</p>
            <p className="text-lg font-bold text-green-600">${totalWinnings}</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4 text-center">
            <TrendingUp className="w-6 h-6 text-blue-600 mx-auto mb-2" />
            <p className="text-sm text-gray-600">Net Profit</p>
            <p className={`text-lg font-bold ${netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              ${netProfit >= 0 ? '+' : ''}{netProfit}
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4 text-center">
            <Trophy className="w-6 h-6 text-yellow-600 mx-auto mb-2" />
            <p className="text-sm text-gray-600">Win Rate</p>
            <p className="text-lg font-bold text-yellow-600">{winRate}%</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4 text-center">
            <Calendar className="w-6 h-6 text-purple-600 mx-auto mb-2" />
            <p className="text-sm text-gray-600">Games Played</p>
            <p className="text-lg font-bold text-purple-600">{gameHistory.length}</p>
          </CardContent>
        </Card>
      </div>

      {/* History Table */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>Game History</CardTitle>
            <div className="flex gap-2">
              {['week', 'month', 'all'].map((period) => (
                <Button
                  key={period}
                  variant={historyPeriod === period ? "default" : "outline"}
                  size="sm"
                  onClick={() => setHistoryPeriod(period)}
                  className={historyPeriod === period ? "bg-yellow-600 hover:bg-yellow-700" : ""}
                >
                  {period === 'week' ? 'This Week' : period === 'month' ? 'This Month' : 'All Time'}
                </Button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {gameHistory.map((game) => (
              <div key={game.id} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-bold text-gray-900">{game.game}</h4>
                      <Badge variant={game.status === 'won' ? 'default' : 'secondary'} 
                             className={game.status === 'won' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                        {game.status === 'won' ? 'Won' : 'Lost'}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-600">{game.date}</p>
                  </div>
                  <div className="text-right">
                    <p className={`font-bold ${game.status === 'won' ? 'text-green-600' : 'text-red-600'}`}>
                      {game.status === 'won' ? `+$${game.winnings}` : `-$${game.betAmount}`}
                    </p>
                    <p className="text-sm text-gray-600">{game.matches} matches</p>
                  </div>
                </div>
                
                <div className="flex flex-wrap gap-1">
                  {game.numbers.map((num, idx) => (
                    <div key={idx} className="w-6 h-6 bg-yellow-600 text-white rounded-full flex items-center justify-center text-xs font-bold">
                      {num}
                    </div>
                  ))}
                  {game.powerball && (
                    <>
                      <span className="text-gray-400 mx-1">+</span>
                      <div className="w-6 h-6 bg-red-600 text-white rounded-full flex items-center justify-center text-xs font-bold">
                        {game.powerball}
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default GameHistory;
